import { useState, useCallback, useEffect } from "react";

interface UseSidebarReturn {
  isCollapsed: boolean;
  toggle: () => void;
  collapse: () => void;
  expand: () => void;
}

const STORAGE_KEY = "sidebar-collapsed";

export function useSidebar(): UseSidebarReturn {
  // Read on the first render rather than in an effect. Reading afterwards
  // painted the sidebar expanded and then collapsed it, which the 200ms width
  // transition turned into a visible swipe on every page load. Safe against
  // hydration mismatch because the auth layout renders a spinner until it has
  // initialised, so the sidebar only ever mounts client-side.
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() =>
    typeof window !== "undefined" &&
    localStorage.getItem(STORAGE_KEY) === "true"
  );
  // No isHydrated guard any more: the first write just puts back the value the
  // initialiser above read, so there is nothing to protect against.
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, isCollapsed.toString());
  }, [isCollapsed]);

  const toggle = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  const collapse = useCallback(() => {
    setIsCollapsed(true);
  }, []);

  const expand = useCallback(() => {
    setIsCollapsed(false);
  }, []);

  return {
    isCollapsed,
    toggle,
    collapse,
    expand,
  };
}
